import Styles from '../../../Styles';
import {Platform, StyleSheet, View} from 'react-native';
import Localized from 'src/constants/AppStrings';
import React from 'react';
import CreditCardInput from 'src/components/elements/funding/CreditCardInput';
import {CreditCard} from 'src/models/CreditCard';
import AVText from 'src/components/elements/AVText';
import Switch from 'src/components/elements/Switch';
import StarIcon from 'src/components/img/svg/StarIcon';
import WarningIconSnackDetails from 'src/components/img/svg/WarningIconSnackDetails';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import {getDescriber} from 'src/components/elements/descriptor/DescriptorType';
import CustomTextInput from 'src/components/elements/CustomTextInput';
import Settings from 'src/Settings';
export function getCardDetailsTitle(): string {
  return Localized.Labels.card_details;
}

export function getCardDetailsView(props: CreditCard): JSX.Element {
  return (
    <React.Fragment>
      <View style={styles.container}>
        <View style={styles.row}>
          <CreditCardInput
            accessibilityLabel={Localized.Labels.card_number}
            label={Localized.Labels.card_number}
            editable={false}
            issuer={props.cardIssuer}
            value={props.name}
            style={{marginBottom: 32}}
            textStyle={styles.textInput}
            outlineStyle={{
              borderRadius: 8,
              borderWidth: 1,
              borderColor: Styles.neutralHues,
            }}
            isRenderCustomText={true}
            showShadow={false}
            testID="qa_fld_CardNumber"
          />
        </View>
        <View style={[styles.row, {marginTop: -30}]}>
          <View style={styles.leftTxtInput}>
            <CustomTextInput
              label={Localized.Labels.expiration_month_full_form}
              value={props.expirationMonth.toString()}
              defaultValue={props.expirationMonth.toString()}
              editable={false}
              accessible={true}
              accessibilityLabel={Localized.Labels.expiration_month_full_form}
              accessibilityValue={{
                text: props.expirationMonth.toString(),
              }}
              accessibilityState={{disabled: true}}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
              showShadow={false}
              testID="qa_fld_ExpirationMonth"
            />
          </View>
          <View style={styles.rightTxtInput}>
            <CustomTextInput
              label={Localized.Labels.expiration_year_full_form}
              value={props.expirationYear.toString()}
              defaultValue={props.expirationYear.toString()}
              editable={false}
              accessible={true}
              accessibilityLabel={Localized.Labels.expiration_year_full_form}
              accessibilityValue={{
                text: props.expirationYear.toString(),
              }}
              accessibilityState={{disabled: true}}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
              showShadow={false}
              testID="qa_fld_ExpirationYear"
            />
          </View>
        </View>
      </View>
    </React.Fragment>
  );
}

export function getCardExpiredView(_isExpired: boolean): JSX.Element {
  const expiredView = _isExpired ? (
    <React.Fragment>
      <View style={styles.expiredStatusView}>
        <WarningIconSnackDetails />
        <AVText
          accessibilityLabel={Localized.Labels.this_card_has_expired}
          accessible={true}
          accessibilityRole="text"
          aria-label={`${Localized.Labels.this_card_has_expired}, text`}
          style={[styles.defaultLabel, {marginLeft: 8}]}
        >
          {Localized.Labels.this_card_has_expired}
        </AVText>
      </View>
    </React.Fragment>
  ) : null;
  return expiredView;
}

export function getDefaultStatusView(
  props: CreditCard,
  defaultTokenStatus: boolean,
  switchAction: (value: boolean) => void,
  _isExpired: boolean,
): JSX.Element {
  if (props.isDefault) {
    return (
      <React.Fragment>
        <View style={styles.statusView}>
          <StarIcon />
          <AVText
            accessible={true}
            accessibilityLabel={
              Localized.Labels.this_card_is_set_as_your_default_and_is_starred
            }
            accessibilityRole="text"
            aria-label={`${Localized.Labels.this_card_is_set_as_your_default_and_is_starred}, text`}
            style={styles.defaultLabel}
          >
            {Localized.Labels.this_card_is_set_as_your_default_and_is_starred}
          </AVText>
        </View>
      </React.Fragment>
    );
  } else {
    if (_isExpired) {
      return null;
    }
    return (
      <React.Fragment>
        <View
          style={[
            styles.defaultStatusView,
            {marginTop: Styles.Spacing.m0, justifyContent: 'flex-end'},
          ]}
        >
          <Switch
            accessibilityLabel={Localized.Labels.set_as_default}
            accessibilityState={{checked: defaultTokenStatus}}
            text={Localized.Labels.set_as_default}
            testID="setAsDefault"
            onValueChange={(value) => switchAction(value)}
            value={defaultTokenStatus}
          />
        </View>
      </React.Fragment>
    );
  }
}

export function getDeleteButton(deleteAction: () => void): JSX.Element {
  return (
    <RoundedAppButton
      buttonType={RoundedAppButtonType.Solid}
      accessibilityLabelValue={Localized.Buttons.delete_card}
      aria-label={Localized.Buttons.delete_card}
      onPress={deleteAction}
      titleText={Localized.Buttons.delete_card}
      buttonViewStyle={styles.deleteButton}
      buttonContainerStyle={styles.deleteButtonContainer}
      textStyle={styles.deleteText}
      testID="qa_fld_DeleteCard"
    />
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    marginHorizontal: Styles.Spacing.m3,
  },
  leftTxtInput: {
    flex: 0.5,
    flexDirection: 'column',
    marginRight: Styles.Spacing.m1,
  },
  statusView: {
    marginVertical: Styles.Spacing.m2,
    backgroundColor: 'rgba(6, 109, 255, 0.15)',
    paddingVertical: 24,
    borderRadius: 8,
    alignItems: 'center',
    flexDirection: 'row',
    paddingRight: 60,
    paddingLeft: 12,
  },
  expiredStatusView: {
    marginHorizontal: Styles.Spacing.m3,
    marginTop: Styles.Heights.touchTargetHeight0,
    marginBottom: 4,
    backgroundColor: 'rgba(215, 38, 61, 0.15)',
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: 12,
  },
  defaultStatusView: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: Styles.Spacing.m1,
    marginTop: Styles.Spacing.m2,
    ...(Platform.OS === 'web' && {
      paddingRight: Styles.Spacing.m4,
    }),
  },
  rightTxtInput: {
    flexDirection: 'column',
    flex: 0.5,
    marginLeft: Styles.Spacing.m1,
  },
  defaultLabel: {
    fontSize: Styles.Fonts.f0 + 2,
    marginLeft: Styles.Spacing.m2,
    marginRight: Styles.Spacing.m1,
    fontFamily:
      Settings.buildType === 'companykitchen'
        ? Styles.FontFamily.latoRegular
        : Styles.FontFamily.robotoRegular,
    fontWeight: '400',
    color: '#111',
  },
  textInput: {
    fontSize: Styles.Fonts.f1 + 2,
    color: Styles.black,
    fontFamily: getDescriber().walletForntStyleText(),
    fontWeight: '400',
    height: 56,
  },
  deleteButton: {
    backgroundColor: Styles.Colors.PayNew.error,
    height: 42,
  },
  deleteButtonContainer: {
    right: Styles.Spacing.m3,
    left: Styles.Spacing.m3,
    bottom: Styles.Spacing.m3,
    position: 'absolute',
  },
  deleteText: {
    paddingHorizontal: 12,
    textAlign: 'center',
    fontSize: Styles.Fonts.f1,
    fontFamily: getDescriber().walletForntStyleText(),
    fontWeight: '700',
    flex: 1,
  },
  row: {
    flexDirection: 'row',
  },
});

export default {
  getCardDetailsView,
  getDeleteButton,
  getDefaultStatusView,
  getCardExpiredView,
  getCardDetailsTitle,
};
